import React, { useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';

import { analyticsService } from '../../../services/analyticsService';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import type { ModSettings } from '@airhead-games/shared/types';

export type CustomWordsEditorProps = {
  modSettings: ModSettings;
  setModSettings: React.Dispatch<React.SetStateAction<ModSettings>>;
  markDirty: () => void;
  canEdit: boolean;
  onRequireAuth: () => void;
};

const CustomWordsEditor: React.FC<CustomWordsEditorProps> = ({
  modSettings,
  setModSettings,
  markDirty,
  canEdit,
  onRequireAuth,
}) => {
  const [newWord, setNewWord] = useState('');

  const addWord = () => {
    if (!canEdit) {
      onRequireAuth();
      return;
    }

    const word = newWord.trim();
    if (!word || modSettings.censorCustomWords.includes(word)) {
      return;
    }

    analyticsService.trackButtonClick('Add Custom Censor Word', {
      link_location: 'library_custom_words',
      word_count: modSettings.censorCustomWords.length + 1,
    });
    setModSettings((current) => ({
      ...current,
      censorCustomWords: [...current.censorCustomWords, word],
    }));
    setNewWord('');
    markDirty();
  };

  const removeWord = (wordToRemove: string) => {
    if (!canEdit) {
      onRequireAuth();
      return;
    }

    analyticsService.trackButtonClick('Remove Custom Censor Word', {
      link_location: 'library_custom_words',
      word_count: Math.max(0, modSettings.censorCustomWords.length - 1),
    });
    setModSettings((current) => ({
      ...current,
      censorCustomWords: current.censorCustomWords.filter(
        (word) => word !== wordToRemove
      ),
    }));
    markDirty();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Input
          value={newWord}
          onChange={(event) => setNewWord(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addWord();
            }
          }}
          placeholder="Type a word to censor"
          maxLength={30}
        />
        <Button type="button" onClick={addWord}>
          <PlusIcon />
          Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {modSettings.censorCustomWords.length > 0 ? (
          modSettings.censorCustomWords.map((word) => (
            <Badge key={word} variant="secondary" className="gap-1 pr-1">
              <span>{word}</span>
              <button
                type="button"
                onClick={() => removeWord(word)}
                className="rounded-full p-0.5 hover:bg-black/10"
                aria-label={`Remove ${word}`}
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">
            No custom words added yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomWordsEditor;
